import capitalize from 'lodash/capitalize';
import find from 'lodash/find';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import { arrayOf, bool, func, shape, string } from 'prop-types';
import React from 'react';

import BulkToggleControls from '../../generic/BulkToggleControls';
import withSelection from '../../generic/Table/with-selection';
import { campaignLineShape, campaignShape } from '../../prop-types';
import getEventTargetValue from '../../utils/get-event-target-value';
import NewCampaignLineForm from '../NewCampaignLineForm';
import ChangeRequestBulkEditor from './ChangeRequestBulkEditor';
import initializeCampaignLineFrom from './initialize-campaign-line';
import NewProductFields from './NewProductFields';
import NewProductSelection from './NewProductSelection';

const initialState = {
  error: null,
  selectedProductOption: null,
  selectedGenders: [],
  selectedCountries: [],
};

class AddProductForm extends React.Component {
  constructor(props) {
    super(props);
    this.state = initialState;
    this.selectProduct = this.selectProduct.bind(this);
    this.updatePricing = this.updatePricing.bind(this);
    this.handleSelectionsChange = this.handleSelectionsChange.bind(this);
    this.handleLineChange = this.handleLineChange.bind(this);
  }

  handleSelectionsChange(options, change) {
    const selected = change.name;

    this.setState(
      { [`selected${capitalize(selected)}`]: options || [] },
      () => {
        const hasGender =
          this.state.selectedProductOption.product.overwriteGender;
        const genders = this.state.selectedGenders;
        const countries = this.state.selectedCountries || [];

        if (isEmpty(countries) || (hasGender && isEmpty(genders)))
          this.changeCampaignLines([]);

        const clKeys = hasGender
          ? flatten(
              countries.map(country => {
                return genders.map(gender => ({
                  country,
                  gender: gender.value,
                }));
              }),
            )
          : countries.map(country => ({ country }));
        this.compileCampaignLines(clKeys);
      },
    );
  }

  handleLineChange(event) {
    const { onChange } = this.props;
    const { name } = event.target;
    const value = getEventTargetValue(event.target);

    onChange({
      target: {
        name: `campaignLines.${name}`,
        value,
      },
    });
  }

  compileCampaignLines(clKeys) {
    const { campaign } = this.props;
    const { campaignLines } = this.props.value;
    const { pricing, selectedProductOption } = this.state;
    const { product } = selectedProductOption;
    let newLines;
    if (!clKeys) {
      newLines = [initializeCampaignLineFrom({ campaign, product, pricing })];
    } else {
      newLines = clKeys.map(cl => {
        const { gender, country } = cl;
        const clObj = find(campaignLines, { gender, country });
        if (clObj) return clObj;
        return initializeCampaignLineFrom({
          campaign,
          country,
          product,
          pricing,
          gender,
        });
      });
    }

    this.changeCampaignLines(newLines);
  }

  changeCampaignLines(value) {
    this.props.onChange({
      target: { name: 'campaignLines', value },
    });
  }

  updatePricing(event) {
    const value = getEventTargetValue(event.target);

    const { campaignLines } = this.props.value;
    const newLines = map(campaignLines, line => {
      return { ...line, pricing: value };
    });

    this.changeCampaignLines(newLines);
    this.setState({ pricing: value });
  }

  selectProduct(option) {
    const { campaign } = this.props;
    const { campaignObjective } = campaign.opportunity;
    const pricing = option.product[`defaultPriceModel${campaignObjective}`];

    this.setState(
      {
        selectedProductOption: option,
        pricing,
        selectedCountries: [],
        selectedGenders: [],
      },
      () => {
        this.changeCampaignLines([]);
      },
    );
  }

  render() {
    const {
      campaign,
      productFilter,
      countryFilter,
      disablePricingUpdate,
      onIsSelected,
      onToggleSelection,
      onToggleSelectionMultiple,
      selectedItems,
      onChange,
    } = this.props;
    const { campaignLines } = this.props.value;
    const { selectedProductOption, selectedCountries, selectedGenders } =
      this.state;

    const configQueries = {
      brandIds: campaign.brandIds,
      productId: selectedProductOption
        ? selectedProductOption.product.id
        : null,
    };

    const hasNoSelectedItems = !selectedItems.length;

    return (
      <>
        <NewProductSelection
          existingCampaignLines={campaign.campaignLines}
          selectedProductOption={selectedProductOption}
          onProductChange={this.selectProduct}
          productFilter={productFilter}
        />
        {selectedProductOption && (
          <NewProductFields
            selectedProduct={selectedProductOption.product}
            countries={selectedCountries}
            genders={selectedGenders}
            onSelectionChange={this.handleSelectionsChange}
            pricing={this.state.pricing}
            onPricingChange={this.updatePricing}
            countryFilter={countryFilter}
            disablePricingUpdate={disablePricingUpdate}
          />
        )}
        <div className="mb-4">
          {campaignLines.length > 0 && (
            <>
              <p className="text-muted mb-4">
                You can optionally add budget to these lines with the Change
                Budget Change Request once this Change Request is granted.
              </p>
              <div className="d-flex mb-2">
                <div>
                  <BulkToggleControls
                    onToggleAll={isSelected => {
                      onToggleSelectionMultiple(
                        campaignLines.map((item, index) => `select-${index}`),
                        isSelected,
                      );
                    }}
                    entities={campaignLines}
                    activeEntities={selectedItems}
                  />
                </div>
                <div className="list-inline ml-auto">
                  <ChangeRequestBulkEditor
                    selectedItems={selectedItems}
                    disabled={hasNoSelectedItems}
                    onChange={onChange}
                  />
                </div>
              </div>
              {campaignLines.map((line, index) => (
                <NewCampaignLineForm
                  onChange={this.handleLineChange}
                  line={line}
                  index={index}
                  key={`cl-${line.country.code}-${line.gender}`}
                  configQueries={configQueries}
                  showCountryHeader={
                    index === 0 ||
                    line.country.code !== campaignLines[index - 1].country.code
                  }
                  onIsSelected={onIsSelected}
                  onToggleSelection={onToggleSelection}
                />
              ))}
            </>
          )}
        </div>
      </>
    );
  }
}

AddProductForm.propTypes = {
  onChange: func.isRequired,
  campaign: campaignShape.isRequired,
  value: shape({
    campaignLines: arrayOf(campaignLineShape).isRequired,
  }).isRequired,
  productFilter: func,
  countryFilter: func,
  disablePricingUpdate: bool,
  onToggleSelectionMultiple: func.isRequired,
  onToggleSelection: func.isRequired,
  onIsSelected: func.isRequired,
  selectedItems: arrayOf(string.isRequired).isRequired,
};

AddProductForm.defaultProps = {
  productFilter: (allProducts, productIds) =>
    allProducts.filter(product => !productIds.includes(product.id)),
  countryFilter: allCountries => allCountries,
  disablePricingUpdate: false,
};

export default withSelection({})(AddProductForm);
